// Pure logic for the "Areas requiring improvement" list + a watchdog that guarantees a scored
// shortfall is never silently dropped from it. DOM-free so both are unit-testable.

import type { SkillBreakdownRow } from '../lib/types.js';
import { SKILL_IDS } from '../lib/skills.js';

/**
 * A REQUIRED skill (expected_score > 0) that was actually scored yet fell short of the manager's
 * expected minimum. This is the ground-truth definition of "observed performance that did not meet
 * expectation" — independent of the derived `attainment` field.
 */
export function isShortfall(s: SkillBreakdownRow): boolean {
  return (
    s.expected_score > 0 &&
    s.evidence_state === 'scored' &&
    s.actual_score !== null &&
    s.actual_score < s.expected_score
  );
}

/**
 * Skills to list under "Areas requiring improvement": required skills (expected_score !== 0) that
 * under-attained, produced no evidence, OR are a scored shortfall. The explicit isShortfall() union
 * is the safety net — a genuine miss must appear even if `attainment` came back null or rounded to
 * 100, which was how the section could previously render empty despite a real shortfall.
 */
export function improvementGaps(rows: SkillBreakdownRow[]): SkillBreakdownRow[] {
  return rows.filter(
    (s) =>
      s.expected_score !== 0 &&
      ((s.attainment !== null && s.attainment < 100) || s.evidence_state === 'no_evidence' || isShortfall(s)),
  );
}

/**
 * Watchdog: scored shortfalls that are MISSING from the shown improvement list. By construction
 * improvementGaps() includes every shortfall, so this should always be empty — a non-empty result
 * means the report would understate the rep's misses and is surfaced as a QC warning at runtime and
 * asserted in tests.
 */
export function missedShortfalls(rows: SkillBreakdownRow[], shown: SkillBreakdownRow[]): SkillBreakdownRow[] {
  const shownIds = new Set(shown.map((s) => s.skill_id)); // key on id, not display name
  return rows.filter((s) => isShortfall(s) && !shownIds.has(s.skill_id));
}

/**
 * Watchdog: scored skills the performance bars can't place — their skill_id isn't in the UI's fixed
 * order, so renderActual() would silently paint nothing (a misleading 0). After the id-join, name
 * drift can no longer cause this; only a skill the UI doesn't yet know (e.g. a newly added scorer)
 * can. Surfaced as a Message Center warning in main.ts and asserted in tests.
 */
export function unplaceableScoredSkills(rows: SkillBreakdownRow[]): SkillBreakdownRow[] {
  return rows.filter((s) => s.evidence_state === 'scored' && s.actual_score !== null && !SKILL_IDS.has(s.skill_id));
}
